/**
 * Ultra-light drop-in replacement for the small subset of framer-motion the
 * site actually uses (fade / slide / scale reveals). Keeps the visual result
 * identical while removing ~36 KB of JavaScript from every page bundle.
 *
 * Supported props: initial, animate, whileInView, transition ({duration, delay,
 * ease}), viewport ({once, amount}). `exit` is accepted and ignored.
 * Supported values: opacity, x, y, scale, filter.
 */
import {
  createElement,
  forwardRef,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

type Vals = {
  opacity?: number;
  x?: number;
  y?: number;
  scale?: number;
  filter?: string;
};

type MotionProps = {
  initial?: Vals | boolean | string;
  animate?: Vals | string;
  whileInView?: Vals;
  exit?: Vals;
  transition?: { duration?: number; delay?: number; ease?: string | number[] };
  viewport?: { once?: boolean; amount?: number };
  style?: CSSProperties;
  children?: ReactNode;
  className?: string;
  [key: string]: unknown;
};

const toStyle = (v?: Vals | boolean | string): CSSProperties => {
  if (!v || typeof v !== "object") return {};
  const transforms: string[] = [];
  if (v.x !== undefined || v.y !== undefined)
    transforms.push(`translate3d(${v.x ?? 0}px, ${v.y ?? 0}px, 0)`);
  if (v.scale !== undefined) transforms.push(`scale(${v.scale})`);
  const style: CSSProperties = {};
  if (v.opacity !== undefined) style.opacity = v.opacity;
  if (v.filter !== undefined) style.filter = v.filter;
  if (transforms.length) style.transform = transforms.join(" ");
  return style;
};

const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function createMotionComponent(Base: ElementType) {
  return forwardRef<HTMLElement, MotionProps>(function Motion(props, ref) {
    const {
      initial,
      animate,
      whileInView,
      exit: _exit,
      transition: transitionProp,
      viewport: viewportProp,
      style: styleProp,
      children: childrenProp,
      ...rest
    } = props;
    const transition = transitionProp as MotionProps["transition"];
    const viewport = viewportProp as MotionProps["viewport"];
    const style = styleProp as CSSProperties | undefined;
    const children = childrenProp as ReactNode;
    const restProps = rest as Record<string, unknown>;

    const target = (whileInView ?? animate) as Vals | undefined;
    const instant = reduceMotion() || !target;
    const [shown, setShown] = useState(!whileInView || instant);
    const nodeRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
      if (!whileInView || shown) return;
      const el = nodeRef.current;
      if (!el) return;
      if (typeof IntersectionObserver === "undefined") {
        setShown(true);
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setShown(true);
            io.disconnect();
          }
        },
        { threshold: viewport?.amount ?? 0.1, rootMargin: "0px 0px -5% 0px" }
      );
      io.observe(el);
      return () => io.disconnect();
    }, [whileInView, shown, viewport?.amount]);

    const duration = transition?.duration ?? 0.4;
    const delay = transition?.delay ?? 0;

    const merged: CSSProperties = {
      ...(instant ? {} : shown ? toStyle(target) : toStyle(initial)),
      ...(instant
        ? {}
        : {
            transition: `opacity ${duration}s ease-out ${delay}s, transform ${duration}s ease-out ${delay}s, filter ${duration}s ease-out ${delay}s`,
            willChange: shown ? undefined : "opacity, transform",
          }),
      ...style,
    };

    return createElement(
      Base,
      {
        ...restProps,
        ref: (node: HTMLElement | null) => {
          nodeRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as { current: HTMLElement | null }).current = node;
        },
        style: merged,
      },
      children
    );
  });
}

const cache = new Map<string, ElementType>();

/** `motion.div` / `motion.form` / `motion(Component)` */
export const motion = new Proxy(
  ((Base: ElementType) => createMotionComponent(Base)) as unknown as Record<
    string,
    ElementType
  > & ((Base: ElementType) => ElementType),
  {
    get(_t, key: string) {
      if (!cache.has(key)) cache.set(key, createMotionComponent(key as ElementType));
      return cache.get(key);
    },
  }
) as ((Base: ElementType) => ElementType) & Record<string, ElementType>;

/** Exit animations are dropped; children simply unmount. */
export const AnimatePresence = ({ children }: { children?: ReactNode }) => <>{children}</>;
